import React, { useRef } from 'react';
import { ProTable, Link, DateDisplay } from 'components/layout';
import { useQuery } from '@apollo/client';
import { GET_PROJECT_APPLICATIONS } from 'gql/projectApplications';
import {
  renderProTableSearchDepartment,
  renderProTableSearchFaculty,
  renderProTableSearchProjectApplicationSetting,
} from 'components/layout/ProTable';
import { ENUM_PROJECT_APPLICATION_STATUS } from 'constants/form';
import { useViewProjectApplication } from 'hooks';
import { getSort, getFundingApplicationStatus } from 'utils';

const AllProjectApplications = () => {
  const ref = useRef();
  const { refetch } = useQuery(GET_PROJECT_APPLICATIONS, {
    variables: {
      pageSize: 0,
    },
  });
  const { level, department, faculty, loading } = useViewProjectApplication();
  const columns = [
    {
      title: 'Funding',
      key: 'setting',
      width: '20%',
      dataIndex: 'setting',
      sorter: true,
      render: (_, { id, setting }) => (
        <Link to={`/project-applications/${id}`}>{setting.title}</Link>
      ),
      colSize: 2,
      renderFormItem: renderProTableSearchProjectApplicationSetting,
    },
    {
      title: 'Application submission date',
      key: 'submissionDate',
      dataIndex: 'firstSubmissionDate',
      sorter: true,
      render: (_, { firstSubmissionDate, id }) => (
        <Link to={`/project-applications/${id}`}>
          <DateDisplay date={firstSubmissionDate} />
        </Link>
      ),
      hideInSearch: true,
    },
    {
      title: 'Application deadline',
      key: 'endDate',
      dataIndex: 'endDate',
      // responsive: ['md'],
      sorter: true,
      render: (_, { setting, id }) => (
        <>
          <Link to={`/project-applications/${id}`}>
            <DateDisplay date={setting.endDate} />
          </Link>
        </>
      ),
      hideInSearch: true,
    },
    {
      title: 'Faculty',
      key: 'faculty',
      dataIndex: 'faculty',
      render: (_, { hostDepartment, id }) => (
        <Link to={`/annual-reports/${id}`}>
          {hostDepartment?.faculty?.abbreviation}
        </Link>
      ),
      sorter: true,
      renderFormItem: (_, { type, defaultValue, ...rest }, form) =>
        renderProTableSearchFaculty(
          _,
          { type, defaultValue, disabled: level !== 'ALL', ...rest },
          form
        ),
      initialValue: faculty,
    },
    {
      title: 'Department',
      key: 'department',
      dataIndex: 'department',
      sorter: true,
      render: (_, { id, hostDepartment }) => (
        <Link to={`/project-applications/${id}`}>
          {hostDepartment?.abbreviation}
        </Link>
      ),
      renderFormItem: (_, { type, defaultValue, ...rest }, form) =>
        renderProTableSearchDepartment(
          _,
          {
            type,
            defaultValue,
            disabled: level !== 'ALL' && level !== 'FACULTY',
            ...rest,
          },
          form
        ),
      initialValue: department,
    },
    {
      title: 'Applicant',
      dataIndex: 'user',
      sorter: true,
      render: (_, { user, id }) => (
        <Link to={`/project-applications/${id}`}>{user.name}</Link>
      ),
      hideInSearch: true,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      width: '30%',
      colSize: 2,
      sorter: true,
      render: (_, { id }) => (
        <Link to={`/project-applications/${id}`}>{_}</Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      valueType: 'select',
      valueEnum: ENUM_PROJECT_APPLICATION_STATUS,
      initialValue: 'ALL',
      sorter: true,
      render: (_, { id }) => (
        <Link to={`/project-applications/${id}`}>{_}</Link>
      ),
    },
  ];

  return (
    <ProTable
      // loading={loading || loadingScope}
      actionRef={ref}
      columns={columns}
      options={{
        density: false,
      }}
      params={{ loading }}
      request={async (params = {}, sort) => {
        const { status, showPriorities } = getFundingApplicationStatus(
          params?.status
        );
        const { data } = await refetch({
          setting: params?.setting,
          pageSize: params?.pageSize,
          current: params?.current,
          department: params?.department,
          faculty: params?.faculty,
          keyword: params?.title,
          showPriorities,
          status,
          sort: getSort(sort),
        });
        const dataSource = data?.projectApplications?.nodes;
        return Promise.resolve({
          data: dataSource,
          total: data?.projectApplications?.totalCount,
          success: true,
        });
      }}
      search={{
        searchText: 'Search',
        labelWidth: 'auto',
      }}
      pagination={{ defaultPageSize: 20, hideOnSinglePage: true }}
      rowKey="id"
      scroll={false}
    />
  );
};

export default AllProjectApplications;
